// Sources UX (21-10) — answer chip + right-side sources panel.
//
// Design (Owner-approved): unified workspace space — the panel shares the
// app background family, separated from the chat ONLY by a 1px hairline
// divider (existing border token). No boxed sidebar, no outer card.
// Source rows are borderless: spacing + subtle gray hover (never cyan fill).
// The panel scrolls independently of the chat (its own overflow container).
//
// Accessibility: chip is a real <button> (Tab/Enter/Space, aria-expanded,
// aria-controls); rows are real <a> (target=_blank rel=noopener noreferrer,
// backend-authoritative URL only); Escape closes the panel; focus rings use
// the accent token.
import { useEffect, useRef } from "react";
import { Globe, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SourceItem } from "@/components/ChatThread";
import { formatSourceDate, pluralSources, sourceDomainLabel } from "@/lib/sources";

// ---------- Chip ----------

interface SourceChipProps {
  count: number;
  expanded: boolean;
  onToggle: () => void;
}

/**
 * «🌐 Использовано N источников» — small capsule at the top of an answer
 * that actually used web sources. N is the deduped, rendered source count
 * (never the raw search result_count). Renders NOTHING when count === 0 —
 * degraded/no-source turns must not show «Использовано 0 источников».
 */
export function SourceChip({ count, expanded, onToggle }: SourceChipProps) {
  if (count <= 0) return null;
  return (
    <button
      type="button"
      data-testid="sources-chip"
      aria-expanded={expanded}
      aria-controls="sources-panel"
      onClick={onToggle}
      className={cn(
        "inline-flex items-center gap-1.5 self-start px-2.5 h-7 rounded-full",
        "border border-hairline bg-elevated/60 text-xs text-secondary",
        "hover:text-primary hover:bg-hoverbg transition-colors",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      )}
    >
      <Globe size={13} className="text-accent shrink-0" />
      <span>
        Использовано {count} {pluralSources(count)}
      </span>
    </button>
  );
}

// ---------- Panel ----------

interface SourcesPanelProps {
  sources: SourceItem[];
  open: boolean;
  onClose: () => void;
  /** 1-based row to scroll into view + briefly highlight (citation click). */
  highlightIndex?: number | null;
}

export function SourcesPanel({ sources, open, onClose, highlightIndex = null }: SourcesPanelProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // Citation click → bring the matching row into view (independent scroll).
  useEffect(() => {
    if (!open || highlightIndex == null) return;
    const el = listRef.current?.querySelector(`[data-source-index="${highlightIndex}"]`);
    // jsdom lacks scrollIntoView — optional-call keeps tests green.
    (el as HTMLElement | null | undefined)?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [open, highlightIndex, sources.length]);

  // Escape closes the panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Empty/degraded: never open as an empty drawer.
  if (!open || sources.length === 0) return null;

  return (
    <aside
      id="sources-panel"
      aria-label="Источники"
      data-testid="sources-panel"
      className={cn(
        // Narrow viewports: right overlay drawer above the chat.
        "fixed inset-y-0 right-0 z-40 w-[min(88vw,360px)] shadow-2xl",
        // Wide (lg+): static side-by-side column, chat keeps its width.
        "lg:static lg:z-auto lg:shadow-none lg:w-[340px] xl:w-[380px] lg:shrink-0",
        // Unified space: same background family, hairline divider only.
        "flex flex-col bg-app border-l border-hairline"
      )}
    >
      <div className="flex items-center justify-between px-4 h-12 shrink-0">
        <span className="text-sm font-medium text-secondary">Источники</span>
        <button
          type="button"
          data-testid="sources-panel-close"
          aria-label="Закрыть источники"
          onClick={onClose}
          className={cn(
            "w-8 h-8 rounded-lg text-tertiary hover:text-primary hover:bg-hoverbg transition-colors",
            "inline-flex items-center justify-center",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          )}
        >
          <X size={16} />
        </button>
      </div>

      {/* Independent vertical scroll — never drags the chat. */}
      <div ref={listRef} className="flex-1 overflow-y-auto px-2 pb-6">
        {sources.map((s, i) => {
          const date = formatSourceDate(s.publishedAt);
          const href = s.url || "#";
          return (
            <a
              key={s.sourceId || s.url || i}
              href={href}
              target={s.url ? "_blank" : undefined}
              rel="noopener noreferrer"
              data-source-index={i + 1}
              data-testid={`source-row-${i + 1}`}
              title={s.title}
              aria-label={`Источник ${i + 1}: ${s.title}`}
              className={cn(
                "block px-2.5 py-3 rounded-lg no-underline transition-colors",
                "hover:bg-hoverbg",
                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
                highlightIndex === i + 1 && "bg-hoverbg"
              )}
              data-highlighted={highlightIndex === i + 1 ? "true" : undefined}
            >
              <div className="flex items-center gap-2 text-xs text-tertiary">
                <span className="w-5 shrink-0 tabular-nums text-secondary font-medium">
                  {i + 1}
                </span>
                <Globe size={12} className="text-accent shrink-0" />
                <span className="truncate">{sourceDomainLabel(s)}</span>
                {s.contentType === "pdf" && (
                  <span
                    data-testid="source-type-badge"
                    className="shrink-0 px-1.5 py-px rounded text-[10px] uppercase tracking-wide text-tertiary border border-hairline"
                  >
                    PDF
                  </span>
                )}
                {date && <span className="ml-auto shrink-0">{date}</span>}
              </div>
              <div className="mt-1 pl-5 text-sm text-primary line-clamp-2 break-words">
                {s.title}
              </div>
              {s.snippet && (
                <div className="mt-1 pl-5 text-xs text-secondary line-clamp-3 break-words">
                  {s.snippet}
                </div>
              )}
            </a>
          );
        })}
      </div>
    </aside>
  );
}
